import { isNotNullish, isNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { formatCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { DateTimeAspect } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { format, max } from 'date-fns';
import { html, nothing } from 'lit';
import { of } from 'rxjs';
import { formatBillingPositionPrice } from '../../../../billingPositions/client/BillingPositionPriceInput.js';
import { bookingActive, bookingFuture } from '../../../../bookings/api/bookingActive.js';
import { BookingAPI } from '../../../../bookings/api/BookingAPI.js';
import { Booking } from '../../../../bookings/entities/Booking.js';
import { SgSnackbar } from '../../../../client/components/ui/sg-snackbar.js';
import { Permission } from '../../../../permissions/roles.js';
import { openUserListSelectionDialog } from '../../../../users/list/client/UserListSelectionDialog.js';
import { freezesMembers, isBillingLocked } from '../../../api/groupBillingLock.js';
import { GroupsAPI } from '../../../api/GroupsAPI.js';
import { Group } from '../../../entities/Group.js';
import { GroupParticipantRow, GroupSepaFilter, GroupsDetailsAPI } from '../../api/GroupsDetailsAPI.js';
import { openAddGroupMemberDialog } from './AddGroupMemberDialog.js';
import { GroupDetailMembersListOperations } from './GroupDetailMembersListOperations.js';
import { MemberInlineAccountingEntryInformation } from './MemberInlineAccountingEntryInformation.js';
import { MemberInlineBillingPositionEdit } from './MemberInlineBillingPositionEdit.js';
import { MemberInlineControls } from './MemberInlineControls.js';
import { MemberInlineSepaPaymentMethod } from './MemberInlineSepaPaymentMethod.js';

const handleAddUser = async (groupId: Maybe<string>, userId: string) => {
  if (!groupId) return;

  const newBooking = new Booking({ groupIDs: [groupId], userID: userId, startDate: todayAsCalendarDay() });

  await guardDialog(() => BookingAPI.upsert([newBooking])({ upserted: 1, skippedExisting: 1 }), {
    success: result =>
      SgSnackbar.show(
        result?.skippedExisting && !result.upserted ? 'Person ist bereits Mitglied.' : 'Buchung erfogreich erstellt!',
      ),
  });
};

const handleMultipleUserSelection = async (groupName: string, groupId: string) => {
  const selection = await openUserListSelectionDialog(`Mitglieder zu ${groupName} hinzufügen`);
  if (!selection) return;

  await guardDialog(() => BookingAPI.addForSelection(groupId, selection)({ upserted: 1, skippedExisting: 1 }), {
    success: result => {
      if (!result) return;
      SgSnackbar.show(
        `${result.upserted} Buchungen erstellt` +
          (result.skippedExisting > 0 ? `, ${result.skippedExisting} waren bereits Mitglied` : ''),
      );
    },
  });
};

const SEPA_FILTER_LABELS: Record<GroupSepaFilter, string> = {
  [GroupSepaFilter.ACCEPTED]: 'Aktzeptiert',
  [GroupSepaFilter.NOT_ACCEPTED]: 'Nicht aktzeptiert',
  [GroupSepaFilter.ZERO_AMOUNT]: 'Betrag 0 (Mandat oder Buchung)',
};

const getCountString = (selectedCount: Maybe<number>, count: Maybe<number>) => {
  if (isNullish(selectedCount) || isNullish(count)) return '...';
  if (selectedCount !== count) return `${selectedCount}/${count}`;
  return `${count}`;
};

/** The "Nur Aktive" filter: members with a booking running today. */
const activeTodayAspect = () =>
  new DateTimeAspect({ customID: 'activeOnDate', mode: 'is', firstValue: new Date(), secondValue: new Date() });

export const GroupDetailsMembers = stateful((props: { groupId: Maybe<string> }) => {
  const user = useObservable(() => currentUser(), []);
  const { value: count } = useQuery(
    () =>
      props.groupId
        ? GroupsDetailsAPI.subscribeMembersCount(
            new AdornisFilter({ aspects: GroupsDetailsAPI.getFilterAspects({ groupID: props.groupId }) }),
          )()
        : of(0),
    [props.groupId],
  );

  const { value: group, loading: groupLoading } = useQuery(
    () =>
      subscribeByID(
        Group,
        props.groupId,
      )({ _id: 1, _class: 1, name: 1, billingLockStichtag: 1, billingLockReleaseDay: 1 }),
    [props.groupId],
  );
  // the server refuses every member and booking edit of a frozen group but ending; the list shows it read only otherwise
  const membersFrozen = !!group && freezesMembers(group, todayAsCalendarDay());

  // "Nur Aktive" is on by default: the list is mostly opened to see who is in the group now
  const [view, setView] = useState<View<typeof Group> | null>(
    new View({
      filter: new AdornisFilter({
        aspects: [...GroupsDetailsAPI.getFilterAspects({ groupID: props.groupId }), activeTodayAspect()],
        sortingDirections: ['asc', 'asc'],
        sortingFields: ['profilDaten.stammdaten.nachname', 'profilDaten.stammdaten.vorname'],
      }),
    }),
  );

  const [updateView, setUpdateView] = useState(0);

  const { value: selectedCount } = useQuery(
    () => (props.groupId ? GroupsDetailsAPI.subscribeMembersCount(view?.filter ?? new AdornisFilter({}))() : of(0)),
    [props.groupId, updateView],
  );
  const { value: priceSumInCents } = useQuery(
    () => (props.groupId ? GroupsDetailsAPI.subscribeMembersPriceSum(view?.filter ?? new AdornisFilter({}))() : of(0)),
    [props.groupId, updateView],
  );

  const options = buildFullOptions({
    class: GroupParticipantRow,
    operation: GroupsDetailsAPI.subscribeMembers,
    isSortable: false,
    isColumnSelectable: false,
    isEntryClickable: false,
    view: view ?? new View({}),
    defaultFieldMapping: {
      number: v => v.toLocaleString('de-DE'),
      Date: v => formatCalendarDay(v, 'dd.MM.yyyy'),
      Datetime: v => format(v, 'dd.MM.yyyy HH:mm'),
    },
    customColumns: [
      {
        customID: 'duration',
        columnName: 'Start/Laufzeit Mitgliedschaft',
        renderItem: (entity: GroupParticipantRow) => {
          const bookingStateBadgeClass = bookingActive(entity.booking)
            ? 'badge-outline badge-success'
            : bookingFuture(entity.booking)
              ? 'badge-outline badge-info'
              : 'badge-outline badge-warning';
          const bookingStateLabel = bookingActive(entity.booking)
            ? 'Aktiv'
            : bookingFuture(entity.booking)
              ? 'Geplant'
              : 'Beendet';
          // the whole runtime over all school years; the bookings come sorted by start, one open end keeps it open
          const runtimeStart = entity.bookings[0]?.startDate;
          const runtimeEnd = entity.bookings.every(booking => booking.endDate)
            ? max(entity.bookings.map(booking => booking.endDate).filter(isNotNullish))
            : null;

          return html`
            <div class="pl-4 flex items-center gap-2">
              <span class=${['badge badge-sm', bookingStateBadgeClass].join(' ')}>${bookingStateLabel}</span>
              <div class="flex flex-col">
                <span>
                  ${runtimeStart ? formatCalendarDay(runtimeStart, 'dd.MM.yyyy') : ''}${
                    runtimeEnd ? ` - ${formatCalendarDay(runtimeEnd, 'dd.MM.yyyy')}` : ''
                  }
                </span>
                ${
                  // one booking is the normal case and says nothing
                  entity.bookings.length > 1
                    ? html`<span class="text-sm text-base-content/70">${entity.bookings.length} Buchungen</span>`
                    : nothing
                }
              </div>
            </div>
          `;
        },
        columnWidth: '14rem',
        selectionSet: { booking: { startDate: 1, endDate: 1 }, bookings: { _id: 1, startDate: 1, endDate: 1 } },
      },

      {
        customID: 'person',
        columnName: 'Person',
        columnWidth: '15rem',
        selectionSet: { user: { debitorennummer: 1, profilDaten: { stammdaten: { vorname: 1, nachname: 1 } } } },
        renderItem: (entity: GroupParticipantRow) => {
          return html`
            <div class="min-w-0 pl-2">
              <div class="truncate text-sm font-medium">
                ${entity.user.profilDaten?.stammdaten?.vorname} ${entity.user.profilDaten?.stammdaten?.nachname}
              </div>
              <div class="truncate text-sm text-base-content/70">${entity.user.debitorennummer}</div>
            </div>
          `;
        },
      },

      {
        columnName: '1. Was soll bezahlt werden?',
        customID: '__billingPositionEdit',
        disabledHref: true,
        columnWidth: 'minmax(16rem, 1fr)',
        selectionSet: {
          group: { billingPosition: { kuerzel: 1, name: 1, priceInCents: 1, rhythm: 1 } },
          booking: {
            _id: 1,
            billingPosition: { priceInCents: 1 },
          },
          billedOn: 1,
        },
        renderItem: (entity: GroupParticipantRow) =>
          MemberInlineBillingPositionEdit({ participantRow: entity, disabled: membersFrozen }),
      },
      {
        columnName: '2. Zahlungsart',
        customID: '__paymentMethod',
        disabledHref: true,
        columnWidth: 'max-content',
        selectionSet: { bookings: { _id: 1 } },
        renderItem: (entity: GroupParticipantRow) =>
          MemberInlineSepaPaymentMethod({ participantRow: entity, disabled: false }),
      },
      {
        columnName: '3. Bezahlt?',
        customID: '__paymentState',
        disabledHref: true,
        columnWidth: 'max-content',
        selectionSet: { bookings: { _id: 1 }, exportedOn: 1, group: { billingPosition: { kuerzel: 1, rhythm: 1 } } },
        renderItem: (entity: GroupParticipantRow) =>
          MemberInlineAccountingEntryInformation({ participantRow: entity, disabled: false }),
      },
      {
        customID: '__actions',
        columnName: '',
        columnWidth: 'max-content',
        selectionSet: {
          _class: 1,
          booking: { _id: 1, _class: 1, userID: 1 },
          // "Buchung beenden" shows what ending them does to their mandates
          bookings: { _id: 1, startDate: 1, endDate: 1 },
          user: { _id: 1, _class: 1 },
        },
        disabledHref: true,
        renderItem: (entity: GroupParticipantRow) =>
          MemberInlineControls({ participantRow: entity, frozen: membersFrozen }),
      },
    ],
  });

  if (!props.groupId || groupLoading) return nothing;

  const today = todayAsCalendarDay();
  const locked = !!group && isBillingLocked(group, today);
  // only a lock export locks a group, so a lock always comes with its billed bookings; by hand it can only be released
  const canRelease =
    locked && !!user?.hasPermission(Permission.ACCEPT_ALL_SEPA_MANDATES_FOR_BILLING_POSITION, ANY_CONTEXT);
  const release = () =>
    guardDialog(() => GroupsAPI.releaseBillingLock(props.groupId ?? '')(), {
      success: () => SgSnackbar.show('Gruppe freigegeben'),
    });
  /**
   * The lock a SEPA lock export leaves behind: while it holds, one-time mandates cannot be
   * accepted and a second export for the same period is refused. It releases on its own when a
   * booking of the group is due again, or by hand.
   */
  const billingLockBadge = () => {
    if (!group?.billingLockStichtag) return nothing;

    const stichtag = formatCalendarDay(group.billingLockStichtag, 'dd.MM.yyyy');
    const release = group.billingLockReleaseDay ? formatCalendarDay(group.billingLockReleaseDay, 'dd.MM.yyyy') : null;
    const text = locked
      ? `In Abrechnung seit ${stichtag} · ${release ? `frei ab ${release}` : 'bis Freigabe'}`
      : `Letzter Export ${stichtag}${release ? ` · freigegeben seit ${release}` : ''}`;

    return html`
      <div
        class="badge ${locked ? 'badge-warning' : 'badge-neutral badge-outline'} gap-1 self-start px-3 py-3 sm:self-auto"
      >
        <span class="icon-[material-symbols--${locked ? 'lock' : 'lock-open'}-outline-rounded]"></span>
        ${text}
      </div>
    `;
  };

  /**
   * The page fills the screen and the table box takes whatever height is left below the filters, so
   * its horizontal scrollbar sits at the bottom edge of the screen instead of below the last row.
   * On a short screen the box keeps a minimum and the page scrolls instead.
   */
  return html`
    <div class="card-container flex-1 min-h-0">
      <div class="card card-body flex-col flex-1 min-h-0">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div class="w-full sm:flex-2">
            ${TextInput({
              placeholder: 'Mitglieder suchen...',
              suffix: html`<span class="icon-[material-symbols--search] text-base line-height-1"></span>`,
              clearable: true,
              onChanged: value => {
                const oldView = view;
                if (!oldView?.filter) return;
                oldView.filter.search = value;
                setView(oldView);
                setUpdateView(updateView + 1);
              },
            })}
          </div>
          <div class="w-full sm:flex-1">
            ${DropdownSelectionInput({
              selectables: Object.values(GroupSepaFilter),
              renderString: (value: GroupSepaFilter) => SEPA_FILTER_LABELS[value],
              clearable: true,
              placeholder: 'SEPA-Mandat:',
              placeholderMode: 'prefix',
              onPicked: value => {
                const oldView = view;
                if (!oldView?.filter) return;
                if (value) oldView.filter.setFilterAspect(new StringAspect({ customID: 'sepaStatus', value }));
                else oldView.filter.removeFilterAspect('sepaStatus');
                setView(oldView);
                setUpdateView(updateView + 1);
              },
            })}
          </div>

          <button
            class="btn w-full sm:w-auto ${
              view?.filter?.getFilterAspect<DateTimeAspect>('activeOnDate') ? 'btn-success' : ''
            }"
            @click=${() => {
              const oldView = view;
              if (!oldView?.filter) return;
              const exists = oldView.filter.getFilterAspect<DateTimeAspect>('activeOnDate');
              if (exists) {
                oldView.filter.removeFilterAspect('activeOnDate');
              } else {
                oldView.filter.setFilterAspect(activeTodayAspect());
              }
              setView(oldView);
              setUpdateView(updateView + 1);
            }}
          >
            Nur Aktive
          </button>

          <div class="badge badge-neutral badge-outline self-start gap-2 px-3 py-3 sm:self-auto">
            <span class="font-semibold text-base-content">${getCountString(selectedCount, count)}</span>
            Mitglieder
          </div>
          <div
            class="badge badge-neutral badge-outline self-start gap-2 px-3 py-3 sm:self-auto"
            title="Was die Buchungen der gefilterten Mitglieder je Abrechnung zahlen, mit ihrem eigenen Preis"
          >
            <span class="font-semibold text-base-content">
              ${isNullish(priceSumInCents) ? '...' : formatBillingPositionPrice(priceSumInCents)}
            </span>
            Summe
          </div>
        </div>
        ${
          group?.billingLockStichtag
            ? html`
                <div class="flex flex-wrap items-center gap-2">
                  ${billingLockBadge()}
                  ${
                    canRelease
                      ? html`
                          <button class="btn btn-sm btn-outline" @click=${release}>
                            <span class="icon-[material-symbols--lock-open-outline-rounded]"></span>
                            Freigeben
                          </button>
                        `
                      : nothing
                  }
                </div>
              `
            : nothing
        }
        <div class="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div class="flex flex-1 flex-col gap-3">
            <div class="flex flex-col gap-2 lg:flex-row lg:items-center">
              <div class="join join-vertical sm:join-horizontal">
                <button
                  class="btn btn-primary join-item"
                  ?disabled=${membersFrozen}
                  @click=${() => openAddGroupMemberDialog(props.groupId, user => handleAddUser(props.groupId, user))}
                >
                  <span class="icon-[material-symbols--person-add-rounded]"></span>
                  Einzelne Person hinzufügen
                </button>

                <button
                  class="btn btn-outline join-item"
                  ?disabled=${membersFrozen}
                  @click=${() => handleMultipleUserSelection(group?.name ?? 'Gruppe', props.groupId ?? '')}
                >
                  <span class="icon-[material-symbols--group-search-outline-rounded]"></span>
                  Mehrere aus Personenliste wählen
                </button>
              </div>
            </div>
          </div>

          <div class="flex justify-start xl:justify-end xl:pl-4">
            ${GroupDetailMembersListOperations({
              groupID: props.groupId,
              groupName: group?.name ?? 'Gruppe',
              currentFilter: view?.filter,
              membersFrozen,
            })}
          </div>
        </div>

        <!-- the scroll container of the table in both directions; also makes its sticky header row stick -->
        <div class="flex flex-col overflow-auto flex-1 min-h-80">
          <div class="w-full min-w-max">${DataTable({ options: options.dataViewOptions })}</div>
        </div>
      </div>
    </div>
  `;
});
